import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, Lock, User, AlertCircle, Eye, EyeOff, Briefcase } from 'lucide-react';
import HandshakeLogo from '../components/HandshakeLogo';
import { PROFESSION_OPTIONS } from '../constants/professions';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  
  const [name, setName] = useState('');
  const [profession, setProfession] = useState('');
  const [customProfession, setCustomProfession] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const finalProfession = profession === 'Other' ? customProfession.trim() : profession;
    if (!finalProfession) {
      setError('Please select your profession');
      return;
    }
    if (profession === 'Other' && !customProfession.trim()) {
      setError('Please specify your profession');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register(name, email, password, finalProfession);
    
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050507] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-md bg-[#FF4500] flex items-center justify-center p-2">
            <HandshakeLogo className="w-full h-full" />
          </div>
          <span className="font-clash font-semibold text-white text-2xl">SettledInFull</span>
        </Link>

        {/* Card */}
        <div className="bg-surface p-8 rounded-md border border-white/8 card-glow">
          <div className="text-center mb-8">
            <h1 className="font-clash text-2xl font-medium text-white mb-2">Create your account</h1>
            <p className="text-white/60">Start recovering payments today</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="register-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="profession" className="text-white/70">Profession</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 z-10 pointer-events-none" />
                <select
                  id="profession"
                  value={profession}
                  onChange={(e) => { setProfession(e.target.value); if (e.target.value !== 'Other') setCustomProfession(''); }}
                  required
                  className="w-full h-10 pl-10 pr-3 rounded-md bg-black/50 border border-white/10 text-white text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FF4500]/40 focus:border-[#FF4500]/40"
                  data-testid="register-profession"
                  style={{ color: profession ? 'white' : 'rgba(255,255,255,0.3)' }}
                >
                  <option value="" disabled className="bg-[#121216] text-white/30">Select your profession</option>
                  {PROFESSION_OPTIONS.map(p => (
                    <option key={p} value={p} className="bg-[#121216] text-white">{p}</option>
                  ))}
                </select>
              </div>
              {profession === 'Other' && (
                <div className="relative mt-2">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    type="text"
                    placeholder="Please specify your profession"
                    value={customProfession}
                    onChange={(e) => setCustomProfession(e.target.value)}
                    required
                    className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                    data-testid="register-custom-profession"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/70">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-10 pr-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                  data-testid="register-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF4500] hover:bg-[#CC3700] text-white py-5"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-surface text-white/40">or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            className="w-full border-white/10 text-white hover:bg-white/5"
            data-testid="google-register"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-white/50 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-[#FF4500] hover:underline">
              Sign in
            </Link>
          </p>

          <Link to="/" className="block mt-3 text-center text-white/40 text-sm hover:text-white/60 transition-colors" data-testid="register-back-home">
            Back to homepage
          </Link>
        </div>

        {/* Disclaimer */}
        <p className="mt-6 text-center text-white/30 text-xs">
          By creating an account, you agree that this platform does not provide legal advice.
        </p>
      </div>
    </div>
  );
}
